/**
 * CORT Lead Form Block
 *
 * Injection block that loads CortFormLoader.js in Custom mode (no default
 * button). Wire up buttons or links elsewhere with window.Cort_modal.open().
 * Skips script injection when no form_id is configured.
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';

const SCRIPT_ID = 'CortFormLoader';
const FORM_DIV_ID = 'CortFormDiv';
const SCRIPT_URL = 'https://api-assets.cort.com/scripts/CortFormLoader.js';

function normalize(value: string | null | undefined) {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function CortLeadFormComponent(_props: BlockProps) {
  const formId = normalize(useBlockToken('form_id'));
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!formId) return;

    if (!document.getElementById(FORM_DIV_ID)) {
      const formDiv = document.createElement('div');
      formDiv.id = FORM_DIV_ID;
      formDiv.hidden = true;
      formDiv.setAttribute('aria-hidden', 'true');
      document.body.appendChild(formDiv);
    }

    document.getElementById(SCRIPT_ID)?.remove();

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (!mountedRef.current) return;
      const loadCortForm = (window as any).loadCortForm;
      if (typeof loadCortForm === 'function') loadCortForm(formId, 'Custom');
    };
    document.body.appendChild(script);

    return () => {
      mountedRef.current = false;
      document.getElementById(SCRIPT_ID)?.remove();
      document.getElementById(FORM_DIV_ID)?.remove();
    };
  }, [formId]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'CortLeadForm',
  displayName: 'CORT Lead Form',
  tokens: {
    form_id: {
      label: 'CORT Form ID',
      type: 'text',
      description:
        'Unique CORT form ID for this property. Required to load the script. Open the modal from a theme CTA with window.Cort_modal.open().',
      required: true,
    },
  },
};

export const cortLeadFormBlock: BlockDefinition = {
  id: 'cort-lead-form',
  name: 'CORT Lead Form',
  description:
    'Loads the CORT furnished-apartments lead form script (Custom mode). Does not render UI. Add buttons or links in your theme and call window.Cort_modal.open() to open the modal.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'global',
  component: CortLeadFormComponent,
  contentSchema,
  tags: ['cort', 'furnished', 'lead', 'form', 'furniture'],
};
